// ============================================================================
// SurveyPreview — apercu VIVANT du sondage (AD-F, porte du PoC survey-preview)
// ============================================================================
// Rejoue le sondage tel que l'employe (ou le directeur) le voit, sur la COPIE
// DE TRAVAIL des questions (template + surcouche resolus) : chaque edition dans
// l'atelier se reflete ici aussitot. Rendu tout-en-un (questions empilees) pour
// un balayage rapide plutot que le wizard pas-a-pas du vrai formulaire. Etat
// local isole : echelles/oui-non cliquables sans rien persister.
// Porte SANS la moindre dependance courriel (STOP-1) et avec les TOKENS DU PARC
// (les ScaleField/YesNoField shadcn du PoC n'existent pas dans le parc — les
// widgets d'apercu locaux ci-dessous en tiennent lieu, memes semantiques).

import { useState } from 'react';
import { Eye } from 'lucide-react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import type { AdminPulseQuestion, PulseSurveyType } from '../../hooks/useAdminQuestions';

const SCALE_VALUES = ['1', '2', '3', '4', '5'] as const;

/** Champs texte natifs du pouls d'equipe (hors modele — parite pulse-form PoC). */
const TEAM_TEXT_FIELDS = ['challenges', 'positives', 'supportNeeded'] as const;

interface PreviewCtx {
  values: Record<string, string>;
  setValue: (name: string, value: string) => void;
}

const chipBase =
  'touch-manipulation rounded-lg border px-3 py-1.5 text-field transition-colors';
const chipOff =
  'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700';
const chipOn =
  'border-brand-400 bg-brand-50 font-medium text-brand-600 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-400';

function PreviewScale({ name, low, high, ctx }: {
  name: string; low: string; high: string; ctx: PreviewCtx;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        {SCALE_VALUES.map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={ctx.values[name] === v}
            onClick={() => ctx.setValue(name, v)}
            className={`${chipBase} min-w-10 ${ctx.values[name] === v ? chipOn : chipOff}`}
          >
            {v}
          </button>
        ))}
      </div>
      {low || high ? (
        <div className="flex justify-between text-chip text-slate-400 dark:text-slate-500">
          <span>{low}</span>
          <span>{high}</span>
        </div>
      ) : null}
    </div>
  );
}

// Valeurs de reponse STOCKEES du oui/non (parite PoC — donnee, pas libelle).
const YES_NO_VALUES = ['oui', 'non'] as const;

function PreviewYesNo({ name, ctx }: { name: string; ctx: PreviewCtx }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5">
      {YES_NO_VALUES.map((v) => (
        <button
          key={v}
          type="button"
          aria-pressed={ctx.values[name] === v}
          onClick={() => ctx.setValue(name, v)}
          className={`${chipBase} ${ctx.values[name] === v ? chipOn : chipOff}`}
        >
          {v === 'oui' ? t('gest:admin.questions.preview.yes') : t('gest:admin.questions.preview.no')}
        </button>
      ))}
    </div>
  );
}

export function SurveyPreview({
  surveyType,
  periodLabel,
  questions,
}: {
  surveyType: PulseSurveyType;
  periodLabel: string;
  questions: AdminPulseQuestion[];
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>({});
  const ctx: PreviewCtx = {
    values,
    setValue: (name, value) => setValues((v) => ({ ...v, [name]: value })),
  };

  const core = questions.filter((q) => q.coreKey != null);
  const extras = questions.filter((q) => q.coreKey == null);

  const renderField = (q: AdminPulseQuestion, name: string) => {
    if (q.questionType === 'scale_1_5') {
      return <PreviewScale name={name} low={q.low ?? ''} high={q.high ?? ''} ctx={ctx} />;
    }
    if (q.questionType === 'yes_no') {
      return <PreviewYesNo name={name} ctx={ctx} />;
    }
    return (
      <textarea
        rows={3}
        aria-label={q.prompt}
        placeholder={q.required ? '' : t('gest:admin.questions.preview.optional')}
        value={ctx.values[name] ?? ''}
        onChange={(e) => ctx.setValue(name, e.target.value)}
        className={GS_COMPONENTS.input}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-900 sm:p-5">
      <div className="border-b border-dashed border-slate-300 pb-3 dark:border-slate-600">
        <p className="flex items-center gap-1.5 text-chip font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          <Eye size={14} aria-hidden="true" />
          {t('gest:admin.questions.preview.eyebrow')}
        </p>
        <p className="text-field font-semibold text-slate-800 dark:text-slate-100">
          {t(`gest:admin.questions.surveyTypes.${surveyType}`)} {'·'} {periodLabel}
        </p>
      </div>

      <div className={`${GS_COMPONENTS.card} mx-auto flex w-full max-w-md flex-col gap-6 p-5`}>
        {core.map((q) => (
          <fieldset key={q.id} className="flex flex-col gap-3">
            <legend className="text-field font-medium text-slate-800 dark:text-slate-100">
              {q.prompt}
            </legend>
            <PreviewScale name={`score_${q.coreKey}`} low={q.low ?? ''} high={q.high ?? ''} ctx={ctx} />
          </fieldset>
        ))}

        {surveyType === 'team_pulse'
          ? TEAM_TEXT_FIELDS.map((f) => (
              <div key={f} className="flex flex-col gap-2">
                <label
                  htmlFor={`preview-${f}`}
                  className="text-field font-medium text-slate-800 dark:text-slate-100"
                >
                  {t(`gest:admin.questions.preview.teamFields.${f}`)}
                </label>
                <textarea
                  id={`preview-${f}`}
                  rows={3}
                  placeholder={t('gest:admin.questions.preview.optional')}
                  value={ctx.values[f] ?? ''}
                  onChange={(e) => ctx.setValue(f, e.target.value)}
                  className={GS_COMPONENTS.input}
                />
              </div>
            ))
          : null}

        {extras.map((q) => (
          <fieldset key={q.id} className="flex flex-col gap-3">
            <legend className="text-field font-medium text-slate-800 dark:text-slate-100">
              {q.prompt}
            </legend>
            {renderField(q, `cq_${q.id}`)}
          </fieldset>
        ))}

        {surveyType === 'member_pulse' ? (
          <div className="flex flex-col gap-2">
            <label
              htmlFor="preview-comment"
              className="text-field font-medium text-slate-800 dark:text-slate-100"
            >
              {t('gest:admin.questions.preview.commentLabel')}
            </label>
            <textarea
              id="preview-comment"
              rows={3}
              placeholder={t('gest:admin.questions.preview.commentPlaceholder')}
              value={ctx.values.comment ?? ''}
              onChange={(e) => ctx.setValue('comment', e.target.value)}
              className={GS_COMPONENTS.input}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
